/**
 * `@react-native-community/geolocation`, answered by the browser.
 *
 * The native module deliberately mirrors the W3C Geolocation API, so this is
 * nearly a pass-through: same option names (`enableHighAccuracy`, `timeout`,
 * `maximumAge`), same position shape, same error callback. The app's two
 * callers - `LocationScreen`'s pin fix and `services/fix.ts`'s SOS locate -
 * both already treat every failure as "no fix", which is exactly what a
 * refused browser prompt produces.
 *
 * One honest difference: browsers only serve geolocation from a secure
 * origin. On http://localhost that is fine; a deployed web build must be
 * HTTPS or every request fails as PERMISSION_DENIED.
 */

type PositionCallback = (position: {
  coords: {
    latitude: number;
    longitude: number;
    accuracy: number;
    altitude: number | null;
    heading: number | null;
    speed: number | null;
  };
  timestamp: number;
}) => void;

type ErrorCallback = (error: {
  code: number;
  message: string;
  PERMISSION_DENIED: number;
  POSITION_UNAVAILABLE: number;
  TIMEOUT: number;
}) => void;

type Options = {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
};

const UNAVAILABLE = {
  code: 2,
  message: 'Geolocation is not available in this browser.',
  PERMISSION_DENIED: 1,
  POSITION_UNAVAILABLE: 2,
  TIMEOUT: 3,
};

const Geolocation = {
  /**
   * On iOS this arms the system prompt; in a browser the prompt belongs to
   * `getCurrentPosition` itself, so there is nothing to do but succeed -
   * which matches how `LocationScreen` already treats the call
   * (fire-and-forget, the read raises the real question).
   */
  requestAuthorization: (success?: () => void, _error?: ErrorCallback): void => {
    success?.();
  },

  getCurrentPosition: (
    onSuccess: PositionCallback,
    onError?: ErrorCallback,
    options?: Options,
  ): void => {
    if (!navigator.geolocation) {
      onError?.(UNAVAILABLE);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      onSuccess,
      onError ?? (() => {}),
      options,
    );
  },

  watchPosition: (
    onSuccess: PositionCallback,
    onError?: ErrorCallback,
    options?: Options,
  ): number => {
    if (!navigator.geolocation) {
      onError?.(UNAVAILABLE);
      return 0;
    }
    return navigator.geolocation.watchPosition(
      onSuccess,
      onError ?? (() => {}),
      options,
    );
  },

  clearWatch: (watchId: number): void => {
    navigator.geolocation?.clearWatch(watchId);
  },

  stopObserving: (): void => {},
};

export default Geolocation;
